import type { ReactNode } from "react";

// Renders a skill's rejection report (why the Scan → Security pipeline rejected it). Shared by the
// upload page (shown the moment an upload fails) and the dashboard's "Rejected" section / skill
// detail page (shown for a rejected skill during its retention window). Pass `footer` for any
// call-site actions (e.g. "Upload a fix").

const titleCase = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Six security dimensions scored by the security agent (see backend schemas.py).
const SECURITY_DIMENSIONS = [
  "prompt_injection",
  "data_exfiltration",
  "code_execution",
  "supply_chain",
  "auth_secrets",
  "network_safety",
] as const;

type Dimension = { score: number; findings: string[] };

// Pull the scored dimensions that actually have findings out of a security report,
// worst score first. Dimensions with no findings are omitted to keep the report focused.
function securityDimensions(report: Record<string, unknown> | undefined): [string, Dimension][] {
  if (!report) return [];
  const out: [string, Dimension][] = [];
  for (const key of SECURITY_DIMENSIONS) {
    const v = report[key];
    if (v && typeof v === "object" && typeof (v as Dimension).score === "number") {
      const findings = Array.isArray((v as Dimension).findings) ? (v as Dimension).findings : [];
      if (findings.length > 0) out.push([key, { score: (v as Dimension).score, findings }]);
    }
  }
  return out.sort((a, b) => a[1].score - b[1].score);
}

// Red below the pass threshold, amber when borderline, green when clean. Darker text in light
// mode (the light-tint background needs a darker foreground for contrast), lighter in dark.
function scoreTone(score: number): string {
  if (score >= 80) return "text-emerald-700 dark:text-emerald-300 bg-emerald-500/15";
  if (score >= 60) return "text-amber-700 dark:text-amber-300 bg-amber-500/15";
  return "text-red-700 dark:text-red-300 bg-red-500/15";
}

// Pull human-readable lines out of a rejection report. Handles the scan shape
// ({issues:[…], summary}), the security shape ({<dimension>:{score, findings:[…]},
// summary}) and the generic error shape ({message}).
function reportMessages(report: Record<string, unknown> | undefined): string[] {
  if (!report) return [];
  const out: string[] = [];
  const pushStr = (v: unknown) => { if (typeof v === "string" && v.trim()) out.push(v); };
  pushStr(report.summary);
  // Top-level string/object arrays (scan issues, generic reasons/errors).
  for (const key of ["issues", "reasons", "errors", "findings"]) {
    const arr = report[key];
    if (Array.isArray(arr)) {
      for (const v of arr) {
        if (typeof v === "string") out.push(v);
        else if (v && typeof v === "object") {
          const o = v as Record<string, unknown>;
          const msg = o.detail || o.message || o.issue || o.title || o.reason;
          out.push(typeof msg === "string" ? msg : JSON.stringify(v));
        }
      }
    }
  }
  // Security dimensions: { prompt_injection: { score, findings: [...] }, … }.
  for (const [k, v] of Object.entries(report)) {
    if (v && typeof v === "object" && Array.isArray((v as Record<string, unknown>).findings)) {
      const dim = titleCase(k);
      for (const f of (v as { findings: unknown[] }).findings) {
        if (typeof f === "string" && f.trim()) out.push(`${dim}: ${f}`);
      }
    }
  }
  pushStr(report.message);
  return [...new Set(out)];
}

export function SkillRejectionReport({
  stage,
  report,
  footer,
  className = "",
}: {
  stage: string;
  report: Record<string, unknown>;
  footer?: ReactNode;
  className?: string;
}) {
  if (stage === "review_incomplete") {
    const raw = typeof report.message === "string" ? report.message : "";
    const findingsLimit = raw.includes("SECURITY_FINDINGS_LIMIT_EXCEEDED");
    return (
      <div className={`rounded-2xl border border-amber-500/40 bg-amber-500/[0.07] p-6 ${className}`}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
            !
          </span>
          <h2 className="text-xl font-semibold text-amber-700 dark:text-amber-300">
            Manual security review required
          </h2>
        </div>
        <p className="mt-3 text-[15px] leading-relaxed text-text/70">
          {findingsLimit
            ? "The scanner produced more findings than the automated review can safely process. Nothing was partially approved."
            : "The automated security review could not complete. This skill remains private and cannot be installed until a complete review succeeds."}
        </p>
        {footer && <div className="mt-6 flex gap-3">{footer}</div>}
      </div>
    );
  }

  // A generic "error" stage means the pipeline hit an unexpected failure (e.g. an upstream API
  // didn't respond). Its report is a raw exception string, which must NEVER be shown — render a
  // single friendly line and nothing else.
  if (stage === "error") {
    return (
      <div className={`rounded-2xl border border-red-500/40 bg-red-500/[0.06] p-6 ${className}`}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-600 dark:text-red-400">
            <XIcon className="h-5 w-5" />
          </span>
          <h2 className="font-display text-xl font-semibold text-red-600 dark:text-red-300">Couldn’t complete the review</h2>
        </div>
        <p className="mt-3 text-[15px] leading-relaxed text-text/70">
          Please try uploading the skill after a few minutes.
        </p>
        {footer && <div className="mt-6 flex gap-3">{footer}</div>}
      </div>
    );
  }

  const where = stage === "security" ? "security review" : stage === "scan" ? "structure scan" : "review";
  const score = typeof report.total === "number" ? report.total : null;
  const dimensions = securityDimensions(report);
  // Show the agent's concise summary; fall back to joining the detail lines for older reports.
  const summary =
    typeof report.summary === "string" && report.summary.trim()
      ? report.summary
      : reportMessages(report).join(" ");
  // Non-security rejections (e.g. structure scan) carry plain issue lines instead of
  // scored dimensions — surface those as a list once the summary is shown above.
  const issues = dimensions.length > 0
    ? []
    : reportMessages(report).filter((m) => m !== summary);
  const sp = report.skillspector as
    | { risk_score?: number; risk_severity?: string; findings_count?: number; error?: string | null }
    | undefined;
  // Structure-scan rejections carry pass/fail booleans — surface them as chips.
  const hasScanChecks = typeof report.structure_ok === "boolean";
  // Inset panels use the app's theme surface vars so they read correctly in BOTH light and dark
  // (a hard-coded dark wash was invisible/inverted in light mode).
  const panel = { background: "var(--bg-elev)", borderColor: "var(--border)" };
  return (
    <div className={`rounded-2xl border border-red-500/40 bg-red-500/[0.06] p-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-600 dark:text-red-400">
          <XIcon className="h-5 w-5" />
        </span>
        <h2 className="font-display text-xl font-semibold text-red-600 dark:text-red-300">Rejected at the {where}</h2>
        {score != null && (
          <span className="rounded-lg bg-red-500/15 px-3 py-1 text-sm font-semibold text-red-700 dark:text-red-300">
            security score {score}/100
          </span>
        )}
      </div>
      <p className="mt-3 text-[15px] leading-relaxed text-text/70">
        This skill wasn’t published. Fix the issues below in the archive, then upload it again.
      </p>

      {/* Structure-scan check chips */}
      {hasScanChecks && (
        <div className="mt-4 flex flex-wrap gap-2">
          <CheckChip ok={report.structure_ok === true} label="SKILL.md structure" />
          <CheckChip ok={report.referenced_files_ok === true} label="Referenced files" />
        </div>
      )}

      {/* SkillSpector (security static scan) */}
      {sp && typeof sp.risk_severity === "string" && (
        <p className="mt-4 text-sm text-text/55">
          SkillSpector scan: <span className="font-medium text-text/80">{sp.risk_severity}</span>
          {typeof sp.risk_score === "number" && ` · ${sp.risk_score}/100`}
          {typeof sp.findings_count === "number" && ` · ${sp.findings_count} finding${sp.findings_count === 1 ? "" : "s"}`}
        </p>
      )}

      {/* Summary */}
      {summary && (
        <p
          className="mt-5 whitespace-pre-wrap rounded-xl border px-4 py-3.5 text-[15px] leading-relaxed text-text/85"
          style={panel}
        >
          {summary}
        </p>
      )}

      {/* Security findings — always visible, two columns on wide screens */}
      {dimensions.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-text/50">
            Security findings ({dimensions.length})
          </h3>
          <div className="grid gap-3 lg:grid-cols-2">
            {dimensions.map(([key, dim]) => (
              <div key={key} className="rounded-xl border p-4" style={panel}>
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-text/90">{titleCase(key)}</span>
                  <span className="text-xs text-text/40">
                    {dim.findings.length} finding{dim.findings.length === 1 ? "" : "s"}
                  </span>
                  <span className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${scoreTone(dim.score)}`}>
                    {dim.score}/100
                  </span>
                </div>
                <ul className="mt-3 space-y-2">
                  {dim.findings.map((f, i) => (
                    <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-text/75">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500/70" />
                      <span className="whitespace-pre-wrap break-words">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Structure / generic issues — always visible list */}
      {issues.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-text/50">
            Issues ({issues.length})
          </h3>
          <ul className="space-y-2.5 rounded-xl border px-4 py-3.5" style={panel}>
            {issues.map((m, i) => (
              <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-text/80">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500/70" />
                <span className="whitespace-pre-wrap break-words">{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {footer && <div className="mt-6 flex gap-3">{footer}</div>}
    </div>
  );
}

function CheckChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ${
        ok ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-red-500/15 text-red-700 dark:text-red-300"
      }`}
    >
      {ok ? <CheckIcon className="h-3.5 w-3.5" /> : <XIcon className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

const ic = "h-5 w-5";
function XIcon({ className = ic }: { className?: string }) { return (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>); }
function CheckIcon({ className = ic }: { className?: string }) { return (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>); }
